import { useState, useEffect, useContext, useCallback, useRef } from "react";
import axios from "axios";
import { Modal, Button, Input, Alert, Spin } from "antd";
import { FilePdfOutlined, SendOutlined, ReloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import mammoth from "mammoth";
import { AuthContext } from "../../context/AuthContext";

const { TextArea } = Input;

export default function SendConfirmationModal({ visible, employee, onClose, onSent }) {
    const { backUrl, authData } = useContext(AuthContext);
    const headers = { Authorization: `Bearer ${authData?.access}` };

    const [remarks, setRemarks] = useState("");
    const [previewedRemarks, setPreviewedRemarks] = useState(null);
    const [preview, setPreview] = useState(null); // { token, preview_url, filename }
    const [renderKind, setRenderKind] = useState(null); // "pdf" | "docx" | null
    const [blobUrl, setBlobUrl] = useState(null);       // object URL, PDF path
    const [docxHtml, setDocxHtml] = useState(null);     // rendered HTML, DOCX path
    const [generating, setGenerating] = useState(false);
    const [rendering, setRendering] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    const blobUrlRef = useRef(null); // track for cleanup across regenerations

    const emp = employee?.employee || {};

    const revokeBlobUrl = () => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
    };

    useEffect(() => {
        if (visible && employee) {
            const initialRemarks = employee.remarks || employee.manager_remarks || "";
            setRemarks(initialRemarks);
            resetPreviewState();
            generatePreview(initialRemarks);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, employee]);

    // Clean up any blob URL when the modal unmounts
    useEffect(() => {
        return () => revokeBlobUrl();
    }, []);

    const resetPreviewState = () => {
        revokeBlobUrl();
        setPreview(null);
        setRenderKind(null);
        setBlobUrl(null);
        setDocxHtml(null);
        setPreviewedRemarks(null);
        setError("");
    };

    const generatePreview = useCallback(
        async (remarksToUse) => {
            if (!employee) return;
            setGenerating(true);
            setError("");
            revokeBlobUrl();
            setBlobUrl(null);
            setDocxHtml(null);
            setRenderKind(null);

            try {
                const empId = emp.id || employee.employee?.id;
                const res = await axios.post(
                    `${backUrl}/api/probation/confirmation-letter/preview/${empId}/`,
                    { manager_remarks: remarksToUse ?? remarks },
                    { headers }
                );
                setPreview(res.data);
                setPreviewedRemarks(remarksToUse ?? remarks);

                await fetchAndRenderFile(res.data.preview_url);
            } catch (err) {
                console.error(err);
                setError(
                    err?.response?.data?.error ||
                    "Failed to generate the confirmation letter preview."
                );
                setPreview(null);
            } finally {
                setGenerating(false);
            }
        },
        [employee, remarks, backUrl, emp.id] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Fetches the file once as a blob, determines its REAL type from the
    // response Content-Type header (don't trust the JSON hint alone), and
    // renders accordingly. This avoids ever pointing an <iframe>/<img> at
    // a .docx URL directly, which is what produced the broken-file icon.
    const fetchAndRenderFile = async (fileUrl) => {
        setRendering(true);
        try {
            const res = await axios.get(fileUrl, {
                responseType: "arraybuffer",
                headers,
            });

            const contentType = (res.headers?.["content-type"] || "").toLowerCase();
            const isPdf = contentType.includes("pdf");

            if (isPdf) {
                const blob = new Blob([res.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                blobUrlRef.current = url;
                setBlobUrl(url);
                setRenderKind("pdf");
            } else {
                const { value: html } = await mammoth.convertToHtml({
                    arrayBuffer: res.data,
                });
                setDocxHtml(html);
                setRenderKind("docx");
            }
        } catch (err) {
            console.error("Preview render failed:", err);
            setRenderKind("failed");
        } finally {
            setRendering(false);
        }
    };

    const handleSend = async () => {
        if (!preview?.token) {
            toast.error("Please generate a preview before sending.");
            return;
        }
        setSending(true);
        try {
            const empId = emp.id || employee.employee?.id;
            await axios.post(
                `${backUrl}/api/probation/confirmation-letter/send/${empId}/`,
                { token: preview.token, manager_remarks: remarks },
                { headers }
            );
            toast.success(`Confirmation letter sent to ${emp.first_name || "employee"}.`);
            onSent && onSent();
            handleClose();
        } catch (err) {
            console.error(err);
            toast.error(
                err?.response?.data?.error || "Failed to send the confirmation letter."
            );
        } finally {
            setSending(false);
        }
    };

    const handleClose = () => {
        resetPreviewState();
        setRemarks("");
        onClose && onClose();
    };

    const remarksAreStale = preview && remarks !== previewedRemarks;
    const busy = generating || rendering;

    return (
        <Modal
            open={visible}
            onCancel={handleClose}
            width={800}
            title={
                <span>
                    <FilePdfOutlined style={{ color: "#0e497b", marginRight: 8 }} />
                    Send Confirmation Letter — {emp.first_name} {emp.last_name}{" "}
                    <span style={{ color: "#888", fontWeight: 400 }}>({emp.emp_code})</span>
                </span>
            }
            footer={[
                <Button key="cancel" onClick={handleClose} disabled={sending}>
                    Cancel
                </Button>,
                <Button
                    key="regenerate"
                    icon={<ReloadOutlined />}
                    onClick={() => generatePreview(remarks)}
                    loading={busy}
                    disabled={sending}
                >
                    Regenerate Preview
                </Button>,
                <Button
                    key="send"
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSend}
                    loading={sending}
                    disabled={busy || !preview || remarksAreStale}
                    style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                >
                    Send Confirmation Letter
                </Button>,
            ]}
        >
            {/* <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Manager Remarks</div>
                <TextArea
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter remarks to include in the confirmation letter"
                    disabled={sending}
                />
                {remarksAreStale && (
                    <Alert
                        style={{ marginTop: 8 }}
                        type="warning"
                        showIcon
                        message="Remarks changed since the last preview. Click 'Regenerate Preview' before sending."
                    />
                )}
            </div> */}

            {error && (
                <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
            )}

            {!busy && renderKind === "docx" && !error && (
                <Alert
                    style={{ marginBottom: 12 }}
                    type="info"
                    showIcon
                    message="PDF conversion isn't available on the server right now — showing a Word document preview instead. The letter will still send correctly."
                />
            )}

            <div
                style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    minHeight: 420,
                    maxHeight: 480,
                    overflowY: "auto",
                    display: "flex",
                    alignItems: renderKind === "docx" ? "stretch" : "center",
                    justifyContent: "center",
                    backgroundColor: "#fafafa",
                    padding: renderKind === "docx" ? 24 : 0,
                }}
            >
                {busy && (
                    <Spin tip={generating ? "Generating preview..." : "Rendering document..."} />
                )}

                {!busy && renderKind === "pdf" && blobUrl && (
                    <iframe
                        title="Confirmation Letter Preview"
                        src={blobUrl}
                        style={{ width: "100%", height: 420, border: "none", borderRadius: 6 }}
                    />
                )}

                {!busy && renderKind === "docx" && docxHtml && (
                    <div
                        style={{
                            background: "#fff",
                            width: "100%",
                            padding: 32,
                            boxShadow: "0 0 0 1px #eee",
                        }}
                        // mammoth output is generated from our own server-rendered
                        // docx, not user-authored input, so this is safe to render.
                        dangerouslySetInnerHTML={{ __html: docxHtml }}
                    />
                )}

                {!busy && renderKind === "failed" && preview && (
                    <div style={{ textAlign: "center", padding: 24 }}>
                        <p>Couldn't render an inline preview for this document.</p>
                        <a
                            href={preview.preview_url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => {
                                // preview_url needs auth; open via blob if we still have it,
                                // otherwise let it 404 through to a login-gated download.
                            }}
                        >
                            <Button>Open / Download to Preview</Button>
                        </a>
                    </div>
                )}

                {!busy && !preview && !error && (
                    <span style={{ color: "#888" }}>No preview generated yet.</span>
                )}
            </div>
        </Modal>
    );
}